/**
 * Etiquetas de parcela para UI: priorizar el número de campo (RAN) sobre el ID interno.
 */

/** Detecta IDs de tierras de uso común (HUM-UC0001 / COR-UC0001, mig 0068). */
const RE_USO_COMUN_ID = /^[A-Z]{2,}-UC0*(\d+)$/i;

export function isUsoComunParcelaId(parcela_id: string | null | undefined): boolean {
  return RE_USO_COMUN_ID.test(String(parcela_id ?? '').trim());
}

/**
 * Número de parcela tal como se usa en campo. Prioriza `num_parcela` en datos;
 * si falta, infiere desde `parcela_id` (p. ej. HUM-P0070 → 70).
 *
 * Las tierras de uso común (HUM-UC####) NO tienen número RAN — devuelve '—'
 * a propósito: el secuencial del ID no es un número de campo y mostrarlo
 * como tal confundiría al técnico.
 */
export function numParcelaCampo(
  parcela_id: string,
  num_parcela?: number | string | null,
): string {
  if (num_parcela != null && String(num_parcela).trim() !== '') {
    const n = Number(num_parcela);
    if (Number.isFinite(n)) return String(n);
  }
  const m = /^[A-Z]{2,}-P0*(\d+)$/i.exec(String(parcela_id ?? '').trim());
  if (m?.[1]) return String(Number.parseInt(m[1], 10));
  return '—';
}

/**
 * Título principal: “Parcela {n}”, “Uso común {n}” (IDs HUM-UC####) o el ID
 * si no hay número interpretable.
 */
export function tituloParcelaCampo(
  parcela_id: string,
  num_parcela?: number | string | null,
): string {
  const uc = RE_USO_COMUN_ID.exec(String(parcela_id ?? '').trim());
  if (uc?.[1]) return `Uso común ${Number.parseInt(uc[1], 10)}`;
  const n = numParcelaCampo(parcela_id, num_parcela);
  return n !== '—' ? `Parcela ${n}` : parcela_id || '—';
}

/**
 * Etiqueta de una sola línea: **parcela primero**, titular segundo — misma
 * jerarquía que `ParcelaInfoBar` (título “Parcela N” arriba, titular debajo).
 * Úsese en overlays, listas y `aria-label` donde haga falta un string único.
 */
export function tituloParcelaConTitular(
  parcela_id: string,
  num_parcela: number | string | null | undefined,
  titular_masked: string | null | undefined,
): string {
  const base = tituloParcelaCampo(parcela_id, num_parcela);
  const t = sanitizeTitular(titular_masked ?? null);
  return t ? `${base} · ${t}` : base;
}

/**
 * Sanitiza un valor de "titular" que viene del Excel inventario.
 * Algunos rows traen JS pegado del visor original (típico:
 * `javascript:PM.Custom.openHyperlink('lay_parcela','par_mov','4018254')`).
 * Devuelve null si el valor está malformado para que el UI muestre "—".
 */
export function sanitizeTitular(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith('javascript:')) return null;
  if (/^https?:\/\//i.test(s)) return null;
  if (/PM\.Custom\.|openHyperlink|<script|=\s*function/i.test(s)) return null;
  if ((s.match(/[<>{}\[\]]/g)?.length ?? 0) > 2) return null;
  return s;
}
